
import Vue from "vue";
import Component from "vue-class-component";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
  OfficialPracticeLightViewModel,
  PracticesClient,
  PracticeType,
  SortOrder,
} from "@/api-client";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import { formatDate } from "@/formatting/dateFormatter";
import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";
import translatePracticeKind from "@/features/shared/PracticeKindTranslator";
import DropdownSelector from "@/components/DropdownSelector.vue";

class Filters {
  searchTerm: string;
  showInvalid!: boolean;
  showValid!: boolean;
  showUnchecked!: boolean;
  type?: PracticeType | null;
  sort?: string;
  sortDir?: SortOrder;

  constructor(
    searchTerm?: string,
    showInvalid?: boolean,
    showValid?: boolean,
    showUnchecked?: boolean,
    type?: PracticeType | null,
    sort?: string,
    sortDir?: SortOrder
  ) {
    this.searchTerm = searchTerm || "";
    this.showInvalid = showInvalid || false;
    this.showValid = showValid || true;
    this.showUnchecked = showUnchecked || false;
    this.type = type || null;
    this.sort = sort;
    this.sortDir = sortDir ?? undefined;
  }
}

@Component({
  components: {
    PopoverInfo,
    TableEmptySlot,
    ValidityStatusBadge,
    ClientsidePagingSelector,
    ServersidePagingSelector,
    FilterCheckbox,
    DropdownSelector,
  },
})
export default class PracticesTable extends Vue {
  practices: OfficialPracticeLightViewModel[] = [];
  SortOrder = SortOrder;
  dataLoaded = false;
  filters = new Filters();
  paging = new ServersidePaging();
  loader = new Loader(true);
  practiceTypes = [
    { value: null, name: this.$ls.Applications_Table_Filters_Option_All },
    {
      value: PracticeType.Seafaring,
      name: this.practiceHeading(PracticeType.Seafaring),
    },
    {
      value: PracticeType.ShipRepair,
      name: this.practiceHeading(PracticeType.ShipRepair),
    },
    {
      value: PracticeType.Pilotage,
      name: this.practiceHeading(PracticeType.Pilotage),
    },
    {
      value: PracticeType.InlandVessel,
      name: this.practiceHeading(PracticeType.InlandVessel),
    },
    {
      value: PracticeType.VtsCenter,
      name: this.practiceHeading(PracticeType.VtsCenter),
    },
  ];

  async created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.filters,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.getPractices();
      },
      { deep: true, immediate: true }
    );
  }

  navigateToDetails(practice: OfficialPracticeLightViewModel) {
    switch (practice.type) {
      case PracticeType.ShipRepair:
        this.$router.push({
          name: "ship-repair-practice-details",
          params: { id: practice.id, seafarerId: practice.seafarerId },
        });
        return;

      case PracticeType.Seafaring:
        this.$router.push({
          name: "seafaring-practice-details",
          params: { id: practice.id, seafarerId: practice.seafarerId },
        });
        return;

      case PracticeType.InlandVessel:
        this.$router.push({
          name: "inland-vessel-practice-details",
          params: { id: practice.id, seafarerId: practice.seafarerId },
        });
        return;

      case PracticeType.Pilotage:
        this.$router.push({
          name: "pilotage-practice-details",
          params: { id: practice.id, seafarerId: practice.seafarerId },
        });
        return;

      case PracticeType.VtsCenter:
        this.$router.push({
          name: "vts-center-practice-details",
          params: { id: practice.id, seafarerId: practice.seafarerId },
        });
        return;

      default:
        throw new Error("Unknown practice type '" + practice.type + "'!");
    }
  }

  async getPractices() {
    this.practices = [];
    const result = await new PracticesClient()
      .setLoader(this.loader)
      .officialPractices(
        this.filters.searchTerm,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.filters.showValid,
        this.filters.showInvalid,
        this.filters.showUnchecked,
        this.filters.type ?? null,
        this.filters.sortDir ?? null,
        this.filters.sort ?? ""
      );
    this.paging.totalCount = result.totalCount;
    this.paging.pageSize = result.pageSize;
    this.practices = result.pageResult;
    this.dataLoaded = true;
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchTerm: this.filters.searchTerm || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        showInvalid: this.filters.showInvalid.toString(),
        showValid: this.filters.showValid.toString(),
        showUnchecked: this.filters.showUnchecked.toString(),
        type: this.filters.type,
        sort: this.filters?.sort ?? "",
        sortDir: this.filters?.sortDir ?? SortOrder.DESC,
      },
    });
  }

  updateSort(ctx: BvTableCtxObject) {
    // this.filters.sort = ctx.sortDesc ? ApplicationSortField.DateSubmittedDesc : ApplicationSortField.DateSubmittedAsc;
    this.filters.sort = ctx.sortBy ?? undefined;
    this.filters.sortDir = ctx.sortDesc ? SortOrder.DESC : SortOrder.ASC;
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const searchTerm: string | undefined = route.query.searchTerm;
    const showInvalid: boolean | undefined = route.query.showInvalid;
    const showValid: boolean | undefined = route.query.showValid;
    const showUnchecked: boolean | undefined = route.query.showUnchecked;
    const sort: string | undefined = route.query.sort;
    const type: PracticeType  | undefined = route.query.type;
    const sortDir: SortOrder.DESC | SortOrder.ASC | undefined =
      route.query.sortDir;

    this.filters = new Filters(
      searchTerm,
      showInvalid,
      showValid,
      showUnchecked,
      type,
      sort,
      sortDir
    );

    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
  }

  practiceHeading(type: PracticeType) {
    switch (type) {
      case PracticeType.ShipRepair:
        return this.$ls.Global_PracticeType_ShipRepairPractice;
      case PracticeType.Seafaring:
        return this.$ls.Global_PracticeType_SeafaringPractice;
      case PracticeType.InlandVessel:
        return (
          this.$ls.Global_PracticeType_InlandVesselPractice ||
          "Siseveelaeva praktika"
        );
      case PracticeType.Pilotage:
        return (
          this.$ls.Global_PracticeType_PilotagePractice || "Lootsipraktika"
        );
      case PracticeType.VtsCenter:
        return (
          this.$ls.Global_PracticeType_VtsCenterPractice || "Töö VTS keskuses"
        );

      default:
        return "Unknown";
    }
  }

  fields: BvTableFieldArray = [
    {
      key: "type",
      label: this.$ls.Practice_Label_PracticeType,
      formatter: (
        value: any,
        key: string,
        item: OfficialPracticeLightViewModel
      ): string => this.practiceHeading(item.type),
    },
    {
      key: "kind",
      label: this.$ls
        .Applications_Requirements_Practices_Table_Label_PracticeKind,
      formatter: (
        value: any,
        key: string,
        item: OfficialPracticeLightViewModel
      ): string => translatePracticeKind(item.kind),
    },
    {
      key: "seafarerName",
      label: this.$ls.Global_Label_Seafarer,
    },
    {
      key: "birthDate",
      label: this.$ls.Global_Label_DateOfBirth,
      formatter: (
        value: any,
        key: string,
        item: OfficialPracticeLightViewModel
      ): string => formatDate(item.birthDate),
    },
    {
      key: "positionName",
      label: this.$ls.Documents_Practices_Label_Position,
      thClass: "w-20",
    },
    {
      key: "issuedAt",
      label: this.$ls.Global_Label_IssueDateShort,
      formatter: (
        value: any,
        key: string,
        item: OfficialPracticeLightViewModel
      ): string => formatDate(item.issuedAt),
    },
    {
      key: "createdAt",
      label: this.$ls.Global_Created,
      sortable: true,
      formatter: (
        value: any,
        key: string,
        item: OfficialPracticeLightViewModel
      ): string => formatDate(item.createdAt),
    },
    {
      key: "statusString",
      label: this.$ls.Global_Label_DocumentState,
    },
    {
      key: "linkIcon",
      label: "",
      thClass: "w-5",
      tdClass: "table-actions",
    },
  ];
}
